/**
 * กล่องยืนยันสำหรับการกระทำที่ย้อนกลับไม่ได้
 *
 * ผู้ว่าจ้างกำหนดว่าการลบต้องถามยืนยันทุกครั้ง ไม่มีข้อยกเว้น
 * จึงทำเป็น component กลางไว้ที่เดียว เพื่อให้ทุกจุดที่ลบได้ถามเหมือนกันหมด
 * และเพื่อไม่ให้ใครเผลอเขียนปุ่มลบที่ลืมถาม
 *
 * ข้อควรรู้เรื่องหน้าจอ
 *   ไม่ใช้ window.confirm() เพราะบนมือถือกล่องนั้นเล็ก อ่านยากกลางแดด
 *   และแสดงรายละเอียดว่ากำลังลบอะไรอยู่ไม่ได้ ซึ่งเป็นส่วนสำคัญที่สุดของการยืนยัน
 *
 *   ปุ่มยืนยันอยู่ล่างสุดและไม่ใช่ปุ่มที่โฟกัสตอนเปิด เพื่อไม่ให้กด Enter รัวแล้วลบไปเลย
 *   ปุ่มยกเลิกเป็นตัวที่ได้โฟกัส กด Escape ก็ปิดได้
 */
import { useEffect, useId, useRef, type ReactNode } from 'react';

interface ConfirmDialogProps {
  open: boolean;
  title: string;
  /** รายละเอียดของสิ่งที่กำลังจะถูกลบ ผู้ใช้ต้องอ่านแล้วรู้ว่าใช่รายการที่ตั้งใจหรือไม่ */
  children: ReactNode;
  confirmLabel: string;
  cancelLabel?: string;
  /** ปิดปุ่มยืนยันไว้จนกว่าเงื่อนไขจะครบ เช่น ยังไม่ได้กรอกเหตุผล */
  confirmDisabled?: boolean;
  busy?: boolean;
  onConfirm: () => void;
  onCancel: () => void;
}

export function ConfirmDialog({
  open,
  title,
  children,
  confirmLabel,
  cancelLabel = 'ยกเลิก',
  confirmDisabled = false,
  busy = false,
  onConfirm,
  onCancel,
}: ConfirmDialogProps) {
  const titleId = useId();
  const cancelRef = useRef<HTMLButtonElement>(null);

  /*
   * เก็บ prop ล่าสุดไว้ใน ref เพื่อให้ effect ด้านล่างไม่ต้องมี onCancel กับ busy เป็น dependency
   *
   * ทำไมต้องทำแบบนี้: ผู้เรียกส่ง onCancel เป็น arrow function ที่สร้างใหม่ทุกรอบ render
   * ถ้าใส่เป็น dependency ตรง ๆ effect จะถูก cleanup แล้วรันใหม่ทุกครั้งที่พิมพ์หนึ่งตัวอักษร
   */
  const latest = useRef({ onCancel, busy });
  useEffect(() => {
    latest.current = { onCancel, busy };
  });

  /*
   * โฟกัสตอนกล่องเปิดเท่านั้น
   *
   * ⚠️ effect นี้ต้องมี dependency แค่ [open] ห้ามเพิ่มอย่างอื่นเด็ดขาด
   *
   * บั๊กที่เคยเกิดจริง: เดิม dependency เป็น [open, busy, onCancel] ซึ่ง onCancel
   * เปลี่ยนค่าทุกรอบ render effect จึงรันใหม่ทุกครั้งที่ผู้ใช้พิมพ์หนึ่งตัวอักษร
   * แล้วดึงโฟกัสจากช่องกรอกกลับมาที่ปุ่มยกเลิก
   * บนเดสก์ท็อปแค่รำคาญ แต่บนมือถือแป้นพิมพ์ปิดทุกตัวอักษร จนพิมพ์เหตุผลไม่ได้เลย
   */
  useEffect(() => {
    if (!open) return;
    // โฟกัสไปที่ปุ่มยกเลิก ไม่ใช่ปุ่มยืนยัน — กดพลาดแล้วต้องไม่ลบ
    cancelRef.current?.focus();
  }, [open]);

  useEffect(() => {
    if (!open) return;

    const onKeyDown = (event: KeyboardEvent) => {
      if (event.key === 'Escape' && !latest.current.busy) latest.current.onCancel();
    };
    document.addEventListener('keydown', onKeyDown);

    // กันไม่ให้หน้าข้างหลังเลื่อนตามนิ้วขณะกล่องเปิดอยู่
    const previousOverflow = document.body.style.overflow;
    document.body.style.overflow = 'hidden';

    return () => {
      document.removeEventListener('keydown', onKeyDown);
      document.body.style.overflow = previousOverflow;
    };
  }, [open]);

  if (!open) return null;

  return (
    <div
      className="dialog-backdrop"
      // คลิกพื้นหลังปิดได้ แต่ไม่ปิดระหว่างกำลังทำงานอยู่ เพราะจะดูเหมือนยกเลิกทั้งที่ส่งไปแล้ว
      onClick={() => {
        if (!busy) onCancel();
      }}
    >
      <div
        className="dialog"
        role="alertdialog"
        aria-modal="true"
        aria-labelledby={titleId}
        onClick={(event) => event.stopPropagation()}
      >
        <h2 id={titleId} className="dialog-title">
          {title}
        </h2>

        <div className="dialog-body stack">{children}</div>

        <div className="dialog-actions">
          <button
            type="button"
            ref={cancelRef}
            className="btn btn-secondary btn-block"
            disabled={busy}
            onClick={onCancel}
          >
            {cancelLabel}
          </button>
          <button
            type="button"
            className="btn btn-danger btn-block"
            disabled={busy || confirmDisabled}
            onClick={onConfirm}
          >
            {busy ? 'กำลังดำเนินการ…' : confirmLabel}
          </button>
        </div>
      </div>
    </div>
  );
}
